import { Application, Container } from '@tencent/alloy-renderer';
import { SlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide';
import { PcEditLogicService } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/pc-edit-logic-service';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { BrowserTarget } from '@tencent/alloy-renderer-browser';
import { RenderPostionType } from '@src/js/constant';
import { reaction } from 'mobx';
import { CANVAS_SLIDE_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { BackgroundService } from '../background/background-service';
import { MasterLayoutService } from '../master-layout/master-layout-service';
import { SlideSizeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-size/slide-size.interface';
import { IPatch } from '@tencent/slide-sdk-constants';
import type { StableLayerManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer-manager';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { StableLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer-service';
import { DataModelChange } from '@tencent/slide-sdk-model';
import ChangesetEngineV3 from '@src/slide/domain/ui-model/changesetManager/changeset-engine-v3';
import { ICanvasSize } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/type';
import { TransparentEventElement } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/event-element';
import {
    IContentSize,
    IViewportSize,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/base/types';
import {
    IPcEditorContentDependentServices,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/pc-content-service.interface';
import { SlideEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/logic/slide-edit-logic';
import {
    IMasterLayoutEditService,
} from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';

import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

const CANVAS_SIZE_SCALE = 3;
const RENDER_GAP = 200;

export interface ISlideEditorProps {
    slide: ISlideModel;
    scale: number;
    isCopyToImage?: boolean; // 是否是生成图片
    stableLayer: StableLayerManager;
    viewportSize: IViewportSize;
    contentSize: IContentSize;
    canvasWrapper: HTMLElement;
    services: IPcEditorContentDependentServices;
    /**
     * 是否需要同步进行渲染
     */
    needSyncRender?: () => boolean;
    /**
     * 回调勾子
     * @param slideComponent
     */
    onSlideWillRender?: (slideModel: ISlideModel) => void;
    onSlideDidRender?: (slideComponent: ISlideComponent) => void;
}

export class SlideEditor {
    public application: Application<any>;
    public slide: ISlideModel;
    public canvasDom: HTMLCanvasElement;
    public slideComponent: SlideComponent;
    public eventComponent: TransparentEventElement;
    public asyncBuild: boolean;
    protected props: ISlideEditorProps;
    private slideSizeType: SlideSizeType;
    private left: number;
    private top: number;
    private hasWrapperBuild: boolean; // 子树是否已经创建完成，
    private needRender: boolean;
    private isDisplay: boolean; // 是否渲染
    /**
     * canvas 画布大小和位置信息
     */
    private canvasSize: ICanvasSize;
    private lastScrollX: number;
    private lastScrollY: number;
    private currentTranslateX = 0;
    private currentTranslateY = 0;
    private needUpdatePosition: boolean;

    public constructor(props: ISlideEditorProps) {
        this.props = props;
        this.slide = props.slide;
        this.hasWrapperBuild = false;
        this.slideSizeType = this.slide.presentationRef?.props.slideSizeType ?? SlideSizeType.SLIDE_SIZE_16_9;
        const canvasSize = this.props.services.editAreaCanvasSizeManager?.getCanvasSize();
        this.left = canvasSize?.slideLeft ?? 0;
        this.top = canvasSize?.slideTop ?? 0;
        this.canvasDom = document.createElement('canvas');
        this.canvasDom.id = `${CANVAS_SLIDE_ID_PREFIX}-${this.slide.key}`;
        this.canvasDom.style.position = 'absolute';
        this.canvasDom.style.left = '0';
        this.canvasDom.style.top = '0';
        this.canvasDom.style.display = 'none';
        const target = new BrowserTarget();
        this.application = new Application(target, {
            interactive: false,
            native: this.canvasDom,
            delayDrawHit: false,
            openPartRender: true,
        });
        this.updateInfo(props.scale, props.contentSize, props.viewportSize);
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        this.application.stage.forceRenderOutsideView = true;
        if (this.slide.childrenConverted) {
            this.doAfterConverted();
        } else {
            // 子元素没有转换完成，先不创建
            this.addConvertObserver();
        }
    }

    /**
     * 监听当前页面的子元素转换完成
     */
    public addConvertObserver(): void {
        const convertedReaction = reaction(
            () => this.slide.childrenConverted,
            (converted) => {
                if (converted) {
                    this.doAfterConverted();
                    // 释放reaction监听
                    convertedReaction?.();
                }
            },
        );
    }

    /**
     * 构建slide 并挂载
     */
    public mountSlide(): void {
        if (!this.hasWrapperBuild) {
            this.asyncBuild = false;
            this.build();
        }
        if (this.needRender) {
            this.clearAllComponentCache(this.application.stage.children as Container[]);
            this.application.stage.transform.scaleX = this.props.scale;
            this.application.stage.transform.scaleY = this.props.scale;
            this.updateCanvasPosition();
            this.slideComponent.display();
            if (this.application.stage.children.length === 0) {
                this.application.stage.addChild(this.slideComponent);
                this.renderEventArea();
            }
            this.eventComponent.renderEventComponent();
            if (!this.props.needSyncRender?.()) {
                this.application.render();
            } else {
                this.application.requestRender();
            }
            this.needRender = false;
        } else {
            // 未变更时不会触发canvas重渲染，此时直接就显示了，业务层需要知道此canvas正常显示了，所以手动触发一次模拟onDidRenderCallback
            this.onDidRenderCallback(this.slideComponent);
            this.props.canvasWrapper.style.transform = `translate(${this.currentTranslateX}px, ${this.currentTranslateY}px)`;
        }
    }

    public renderEventArea() {
        if (!this.eventComponent) {
            this.eventComponent = new TransparentEventElement({});
            this.eventComponent.x = 0;
            this.eventComponent.y = 0;
            this.application.stage.addChildAt(this.eventComponent, 0);
        } else {
            this.eventComponent.renderEventComponent();
        }
    }

    /**
     * slide变为当前页，树如果没有创建好需要切换为同步创建，并挂载到application上
     */
    public display(): void {
        this.isDisplay = true;
        this.mountSlide();
        this.canvasDom.style.display = 'block';
        if (!this.application.interactive) {
            this.application.interactive = true;
            requestAnimationFrame(() => {
                if (this.application.hitView) {
                    this.application.stage.renderHit(this.application.hitView);
                }
            });
        }
    }

    /**
     * 页面隐藏
     */
    public hidden(): void {
        this.isDisplay = false;
        this.canvasDom.style.display = 'none';
        this.application.interactive = false;
    }

    public dispose(): void {
        this.application.view.width = 0;
        this.application.view.height = 0;
        this.application.dispose();
        this.slideComponent?.dispose?.();
        this.eventComponent?.dispose?.();
        this.canvasDom.parentNode?.removeChild(this.canvasDom);
        // 重置slide渲染完成promise
        this.getSlideRenderStateManager()
            ?.getStatePoints()[IStatePointEnum.editSlideRender].resetEditSlideRenderCompletePromise(this.slide);
    }

    /**
     * 当前页更新需要的一些信息
     * @param scale
     * @param contentSize
     * @param viewportSize
     */
    public updateInfo(scale: number, contentSize: IContentSize, viewportSize: IViewportSize): void {
        this.updateCanvasSize(contentSize, viewportSize);
        const slideSizeType = this.slide.presentationRef?.props.slideSizeType ?? SlideSizeType.SLIDE_SIZE_16_9;
        const canvasSize = this.props.services.editAreaCanvasSizeManager?.getCanvasSize();
        const slideLeft = canvasSize?.slideLeft ?? 0;
        const slideTop = canvasSize?.slideTop ?? 0;
        this.props.contentSize = contentSize;
        this.props.viewportSize = viewportSize;
        // 大画布开启视图外内容不参与渲染
        this.application.notRenderOutsideView = !this.isSmallCanvas();
        if (this.application.width === this.canvasSize.width
            && this.application.height === this.canvasSize.height
            && Math.abs(this.props.scale - scale) < 1e-6
            && this.slideSizeType === slideSizeType
            && this.left === slideLeft
            && this.top === slideTop
        ) {
            return;
        }
        this.props.scale = scale;
        this.slideSizeType = slideSizeType;
        this.left = slideLeft;
        this.top = slideTop;
        this.application.width = this.canvasSize.width;
        this.application.height = this.canvasSize.height;
        this.needRender = true;
        this.needUpdatePosition = true;
    }

    public updatePosition() {
        if (this.isSmallCanvas()) {
            return;
        }

        if (this.needUpdatePosition || this.isVerticalScrollToEdge() || this.isHorizontalScrollToEdge()) {
            this.updateCanvasPosition();
            this.needUpdatePosition = false;
        }
    }

    public getBackgroundAppService = (): BackgroundService | undefined => this.props.stableLayer.backgroundManager
        .getBackgroundFromCache(this.slide);

    public getMasterAppService = (): MasterLayoutService | undefined => this.props.stableLayer.masterManager
        .getMasterFromCache(this.slide.getRelationMasterModel());

    public getLayoutAppService = (): MasterLayoutService | undefined => this.props.stableLayer.layoutManager
        .getLayoutFromCache(this.slide.getRelationLayoutModel());

    public getStableLayerService = (): StableLayerService | undefined => this.props.stableLayer
        .getStableLayerService(this.slide);

    public getScale(): number {
        return this.props.scale;
    }

    /**
     * 不通过changeSetEngine的变化来更新父组件，直接强制刷新
     * @param changesetInfo 变化信息
     * @returns
     */
    public forceReloadWithoutSelfChange = (changesetInfo?: IPatch | DataModelChange): void => {
        const changesetInfoV3 = changesetInfo as DataModelChange;
        const needReload = ChangesetEngineV3.hasChangeAtPath(changesetInfoV3, 'showMasterSp');
        if (needReload) {
            this.props.stableLayer?.reloadMasterComponent?.(this.slide);
        }
    };

    protected buildStableLayer(): void {
        this.props.stableLayer?.buildStableLayer({
            slideModel: this.slide,
            masterModel: this.slide.type === UIModelType.MASTER ? undefined : this.slide.getRelationMasterModel(),
            layoutModel: this.slide.type === UIModelType.LAYOUT ? undefined : this.slide.getRelationLayoutModel(),
        });
    }

    /**
     * 根据页面当前的滚动距离计算画布的偏移，并更新渲染树位置
     * @private
     */
    private updateCanvasPosition() {
        const { currentScrollX, currentScrollY } = this.getPcScrollService();
        const translateX = this.isSmallCanvas() ? 0
            : Math.min(
                Math.max(-currentScrollX + this.canvasSize.left, 0),
                this.props.contentSize.width - this.canvasSize.width,
            );
        const translateY = this.isSmallCanvas() ? 0
            : Math.min(
                Math.max(-currentScrollY + this.canvasSize.top, 0),
                this.props.contentSize.height - this.canvasSize.height,
            );
        this.props.canvasWrapper.style.transform = `translate(${translateX}px, ${translateY}px)`;
        const canvasSize = this.props.services.editAreaCanvasSizeManager?.getCanvasSize();
        const slideLeft = canvasSize?.slideLeft ?? 0;
        const slideTop = canvasSize?.slideTop ?? 0;
        this.slideComponent.x = (slideLeft - translateX) / this.props.scale;
        this.slideComponent.y = (slideTop - translateY) / this.props.scale;
        this.lastScrollX = currentScrollX;
        this.lastScrollY = currentScrollY;
        this.currentTranslateX = translateX;
        this.currentTranslateY = translateY;
        this.getPcScrollService().updateCurrentCanvasTranslate(translateX, translateY);
    }

    private isSmallCanvas() {
        return this.props.contentSize.width === this.canvasSize.width
            && this.props.contentSize.height === this.canvasSize.height;
    }

    private updateCanvasSize(contentSize: IContentSize, viewportSize: IViewportSize) {
        const { width, height } = contentSize;
        const canvasWidth = Math.round(Math.min(viewportSize.width * CANVAS_SIZE_SCALE, width));
        const canvasHeight = Math.round(Math.min(viewportSize.height * CANVAS_SIZE_SCALE, height));

        // 为画布设置负向偏移实现居中效果，最大值为0
        const left = Math.min(-(canvasWidth - viewportSize.width) / 2, 0);
        const top = Math.min(-(canvasHeight - viewportSize.height) / 2, 0);

        this.canvasSize = {
            width: canvasWidth,
            height: canvasHeight,
            left,
            top,
        };
    }

    private isVerticalScrollToEdge(): boolean {
        const { currentScrollY } = this.getPcScrollService();

        const top = -this.lastScrollY - this.currentTranslateY;
        const bottom = this.canvasSize.height - this.props.viewportSize.height - top;

        const isScrollToBottom = (this.lastScrollY - currentScrollY > 0)
            && this.currentTranslateY < this.props.contentSize.height - this.canvasSize.height
            && Math.abs(this.lastScrollY - currentScrollY) >= (bottom - RENDER_GAP);
        const isScrollToTop = (this.lastScrollY - currentScrollY < 0) && this.currentTranslateY > 0
            && Math.abs(this.lastScrollY - currentScrollY) >= (top - RENDER_GAP);

        return isScrollToBottom || isScrollToTop;
    }


    private isHorizontalScrollToEdge(): boolean {
        const { currentScrollX } = this.getPcScrollService();

        const left = -this.lastScrollX - this.currentTranslateX;
        const right = this.canvasSize.width - this.props.viewportSize.width - left;


        const isScrollToRight = (this.lastScrollX - currentScrollX > 0)
            && this.currentTranslateX < this.props.contentSize.width - this.canvasSize.width
            && Math.abs(this.lastScrollX - currentScrollX) >= (right - RENDER_GAP);
        const isScrollToLeft = (this.lastScrollX - currentScrollX < 0) && this.currentTranslateX > 0
            && Math.abs(this.lastScrollX - currentScrollX) >= (left - RENDER_GAP);

        return isScrollToRight || isScrollToLeft;
    }


    // 创建component子树，不需要挂载到application上
    private build(): void {
        if (this.hasWrapperBuild) return;
        this.buildSlideComponent();
        this.hasWrapperBuild = true;
    }

    private doAfterConverted(): void {
        const isCurrentSlide = this.isCurrentSlide();
        this.asyncBuild = !isCurrentSlide;
        this.build();
        this.buildStableLayer();
        if (this.isDisplay) {
            // 如果是正在渲染过程中再次触发渲染。
            this.needRender = true;
            this.display();
        }
    }

    private isCurrentSlide(): boolean {
        return this.slide === this.props.services.selectionService.currentSlide;
    }

    private getApplication = () => this.application;

    /**
     * 创建Slide区域
     * @private
     */
    private buildSlideComponent() {
        const logicService = new PcEditLogicService();
        const canvasSize = this.props.services.editAreaCanvasSizeManager?.getCanvasSize();
        const slideLeft = canvasSize?.slideLeft ?? 0;
        const slideTop = canvasSize?.slideTop ?? 0;
        this.slideComponent = new SlideComponent({
            isMasterEdit: this.getMasterLayoutEditService()?.isMasterEditType,
            slideModel: this.slide,
            getPageScale: () => this.props.scale,
            getApplication: this.getApplication,
            forceReloadWithoutSelfChange: this.forceReloadWithoutSelfChange,
            region: RenderPostionType.CANVAS,
            isCopyToImage: this.props.isCopyToImage,
            fromScale: 0,
            logicService,
            editLogic: new SlideEditLogic(this.props.services.editorInstanceService),
            asyncBuild: this.asyncBuild,
            onWillRenderCallback: this.onWillRenderCallback,
            onDidRenderCallback: this.onDidRenderCallback,
            infrastructureService: this.props.services.infrastructureService,
            uiDataService: this.props.services.uiDataService,
            selectionService: this.props.services.selectionService,
            mediaManager: this.props.services.mediaManager,
            animManager: this.props.services.animManager,
            exportImageManager: this.props.services.exportImageManager,
            rightSideBarManager: this.props.services.rightSideBarManager,
            attachmentManager: this.props.services.attachmentManager,
            editingStateManager: this.props.services.editingStateManager,
            formatBrushManager: this.props.services.formatBrushManager,
            addonManager: this.props.services.addonManager,
            editorAnimPreviewManager: this.props.services.getEditorAnimPreviewManager?.(),
            previewService: this.props.services.previewService,
            textEditorStateService: this.props.services.textEditorStateService,
            historyManager: this.props.services.historyManager,
            copyPasteImageService: this.props.services.copyPasteImageService,
            masterLayoutEditService: this.props.services.masterLayoutEditService,
            fontManager: this.props.services.fontManager,
            linkManager: this.props.services.linkManager,
            accessibilityManager: this.props.services.accessibilityManager,
            undoRedoService: this.props.services.undoRedoService,
            textSelectionUndoRedo: this.props.services.textSelectionUndoRedo,
            editAreaCanvasSizeManager: this.props.services.editAreaCanvasSizeManager,
            gifService: this.props.services.gifService,
            renderEditorBackground: () => {
                this.props.stableLayer.backgroundManager.renderBackgroundByCache(this.slide);
            },
        });
        this.slideComponent.x = slideLeft / this.props.scale;
        this.slideComponent.y = slideTop / this.props.scale;
    }

    /**
     * alloy-render内部基于动画性能考虑会复用缓存进行图形缩放（0.5-1），造成图片模糊
     * 所以手动缩放时先清除所有图形缓存
     * @param children
     */
    private clearAllComponentCache = (children: Array<Container>) => {
        if (this.application.stage.transform.scaleX === this.props.scale) {
            return;
        }

        (children || []).forEach((child) => {
            if (child?.cache?.isValid) {
                child.cache.clear();
            }
            if (child.children) {
                this.clearAllComponentCache(child.children as Container[]);
            }
        });
    };

    private onWillRenderCallback = (slideComponent: SlideComponent) => {
        this.props.onSlideWillRender?.(slideComponent.options.slideModel);
    };

    private onDidRenderCallback = (slideComponent: SlideComponent) => {
        this.props.onSlideDidRender?.(slideComponent);
    };

    private getPcScrollService() {
        return this.props.services.pcScrollService;
    }

    private getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
        return this.props.services?.masterLayoutEditService;
    }

    private getSlideRenderStateManager() {
        return this.props.services.slideListRenderState;
    }
}
